import {
  HighwayArea,
  HighwayPoiSpec,
  ManualArea,
  SingularStationArea,
  StationaryArea,
  RackingArea,
  Obstacle,
  Flow,
  UsageProfile,
  LassieParams,
  LassieMiniParams,
  ShuttleRackParams,
  SimulationConfig,
  HikStorageParams,
} from '@thive/fddm-types';

/**
 * @description maps preference keys to their corresponding types in the FDDM, used for type safety when getting/setting preferences
 *
 * Try to keep them 1/1 with where they are in the FDDM
 * @example 'highwayArea.vehicleLimit' corresponds to FloorplanRevision['design']['areas'][areaType]['vehicleLimit']
 */
export type FddmPreferenceValues = {
  // highway area
  'highwayArea.vehicleLimit': HighwayArea['vehicleLimit'];
  'highwayArea.segment.width': HighwayArea['segments'][number]['leftSideWidth'];
  'highwayArea.highwayPoiSpecs.horizontalSpacing.minGap': HighwayPoiSpec['horizontalSpacing']['minGap'];
  'highwayArea.highwayPoiSpecs.horizontalSpacing.minMargin': HighwayPoiSpec['horizontalSpacing']['minMargin'];
  'highwayArea.highwayPoiSpecs.verticalSpacing.minGap': HighwayPoiSpec['verticalSpacing']['minGap'];
  'highwayArea.highwayPoiSpecs.crabSetting': NonNullable<HighwayPoiSpec['crabSetting']>;
  'highwayArea.highwayPoiSpecs.curvature': NonNullable<HighwayPoiSpec['curvature']>;

  'highwayArea.highwayPoiSpecs.verticalSpacing.minMargin': HighwayPoiSpec['verticalSpacing']['minMargin'];
  'highwayArea.highwayPoiSpecs.roadType': HighwayPoiSpec['roadType'];
  'highwayArea.connectNeighbors': HighwayArea['connectNeighbors'];
  'highwayArea.connectUturns': HighwayArea['connectUturns'];
  'highwayArea.highwayPoiSpecs.horizontalDistribution': HighwayPoiSpec['horizontalDistribution'];
  'highwayArea.highwayPoiSpecs.verticalDistribution': HighwayPoiSpec['verticalDistribution'];

  // manual area
  'manualArea.loadHandlingType': ManualArea['loadHandlingType'];
  'manualArea.stationSpec.z': ManualArea['stationSpec']['z'];
  'manualArea.stationSpec.loadHandlingDurationStrategyInSimulation': ManualArea['stationSpec']['loadHandlingDurationStrategyInSimulation'];
  'manualArea.stationSpec.loadDurationOverrideInSimulation': ManualArea['stationSpec']['loadDurationOverrideInSimulation'];
  'manualArea.stationSpec.unloadDurationOverrideInSimulation': ManualArea['stationSpec']['unloadDurationOverrideInSimulation'];

  // singular station area
  'singularStationArea.constellationSpec.stacking': SingularStationArea['constellationSpec']['stacking'];
  'singularStationArea.constellationSpec.distribution': SingularStationArea['constellationSpec']['distribution'];
  'singularStationArea.constellationSpec.angle': SingularStationArea['constellationSpec']['angle'];
  'singularStationArea.constellationSpec.spacing.minGap': SingularStationArea['constellationSpec']['spacing']['minGap'];
  'singularStationArea.constellationSpec.spacing.minMargin': SingularStationArea['constellationSpec']['spacing']['minMargin'];
  'singularStationArea.loadHandlingPoiSpecs.angle': SingularStationArea['loadHandlingPoiSpecs'][number]['angle'];
  'singularStationArea.loadHandlingPoiSpecs.deltaX': SingularStationArea['loadHandlingPoiSpecs'][number]['deltaX'];
  'singularStationArea.loadHandlingPoiSpecs.deltaY': SingularStationArea['loadHandlingPoiSpecs'][number]['deltaY'];
  'singularStationArea.loadHandlingPoiSpecs.alwaysFitStation': SingularStationArea['loadHandlingPoiSpecs'][number]['alwaysFitStation'];
  'singularStationArea.loadHandlingPoiSpecs.crabSetting': SingularStationArea['loadHandlingPoiSpecs'][number]['crabSetting'];
  'singularStationArea.loadHandlingPoiSpecs.curvature': SingularStationArea['loadHandlingPoiSpecs'][number]['curvature'];
  'singularStationArea.loadHandlingPoiSpecs.canBeUsedForParking': SingularStationArea['loadHandlingPoiSpecs'][number]['canBeUsedForParking'];
  'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority': NonNullable<
    SingularStationArea['loadHandlingPoiSpecs'][number]['parkingExtension']
  >['priority'];
  'singularStationArea.stationSpec.z': SingularStationArea['stationSpec']['z'];
  'singularStationArea.stationSpec.loadHandlingDurationStrategyInSimulation': SingularStationArea['stationSpec']['loadHandlingDurationStrategyInSimulation'];
  'singularStationArea.stationSpec.loadDurationOverrideInSimulation': SingularStationArea['stationSpec']['loadDurationOverrideInSimulation'];
  'singularStationArea.stationSpec.unloadDurationOverrideInSimulation': SingularStationArea['stationSpec']['unloadDurationOverrideInSimulation'];
  'singularStationArea.stationSpec.processingExtension.processingDurationInSimulation': NonNullable<
    SingularStationArea['stationSpec']['processingExtension']
  >['processingDurationInSimulation'];
  'singularStationArea.connectNeighbors': SingularStationArea['connectNeighbors'];

  // stationary area
  'stationaryArea.constellationSpec.stacking': StationaryArea['constellationSpec']['stacking'];
  'stationaryArea.constellationSpec.distribution': StationaryArea['constellationSpec']['distribution'];
  'stationaryArea.constellationSpec.angle': StationaryArea['constellationSpec']['angle'];
  'stationaryArea.constellationSpec.spacing.minGap': StationaryArea['constellationSpec']['spacing']['minGap'];
  'stationaryArea.constellationSpec.spacing.minMargin': StationaryArea['constellationSpec']['spacing']['minMargin'];
  'stationaryArea.parkingExtension.priority': NonNullable<
    StationaryArea['parkingExtension']
  >['priority'];
  'stationaryArea.chargingExtension.priority': NonNullable<
    StationaryArea['chargingExtension']
  >['priority'];
  'stationaryArea.insertionExtension.waitDurationInSimulation': NonNullable<
    StationaryArea['insertionExtension']
  >['waitDurationInSimulation'];
  'stationaryArea.extractionExtension.waitDurationInSimulation': NonNullable<
    StationaryArea['extractionExtension']
  >['waitDurationInSimulation'];
  'stationaryArea.stationaryPoiSpecs.crabSetting': NonNullable<
    StationaryArea['stationaryPoiSpecs'][number]['crabSetting']
  >;
  'stationaryArea.stationaryPoiSpecs.curvature': NonNullable<
    StationaryArea['stationaryPoiSpecs'][number]['curvature']
  >;
  'stationaryArea.connectNeighbors': StationaryArea['connectNeighbors'];

  // racking area
  'rackingArea.rackType': RackingArea['rackType'];
  'rackingArea.rackingLoadHandlingType': RackingArea['rackingLoadHandlingType'];
  'rackingArea.stacking': RackingArea['stacking'];
  'rackingArea.margin': RackingArea['margin'];
  'rackingArea.offsetDriving': RackingArea['offsetDriving'];
  'rackingArea.alongsideExtension.aisleWidth': NonNullable<
    RackingArea['alongsideExtension']
  >['aisleWidth'];
  'rackingArea.alongsideExtension.poiDirection': NonNullable<
    RackingArea['alongsideExtension']
  >['poiDirection'];
  'rackingArea.insideExtension.forwardParking': NonNullable<
    RackingArea['insideExtension']
  >['forwardParking'];
  'rackingArea.crabSetting': NonNullable<RackingArea['crabSetting']>;
  'rackingArea.curvature': NonNullable<RackingArea['curvature']>;
  'rackingArea.connectNeighbors': RackingArea['connectNeighbors'];
  'rackingArea.canBeUsedForParking': RackingArea['canBeUsedForParking'];
  'rackingArea.parkingExtension.priority': NonNullable<RackingArea['parkingExtension']>['priority'];

  // Rack
  'rackingArea.rackSpec.bayLength': RackingArea['rackSpec']['bayLength'];
  'rackingArea.rackSpec.bayDepth': RackingArea['rackSpec']['bayDepth'];
  'rackingArea.rackSpec.bayHeight': RackingArea['rackSpec']['bayHeight'];
  'rackingArea.rackSpec.beamLength': RackingArea['rackSpec']['beamLength'];
  'rackingArea.rackSpec.beamHeight': RackingArea['rackSpec']['beamHeight'];
  'rackingArea.rackSpec.clearanceSide': RackingArea['rackSpec']['clearanceSide'];
  'rackingArea.rackSpec.clearanceInBetween': RackingArea['rackSpec']['clearanceInBetween'];
  'rackingArea.rackSpec.clearanceTop': RackingArea['rackSpec']['clearanceTop'];
  'rackingArea.rackSpec.clearanceDepth': RackingArea['rackSpec']['clearanceDepth'];
  'rackingArea.rackSpec.overhangFront': RackingArea['rackSpec']['overhangFront'];
  'rackingArea.rackSpec.overhangBack': RackingArea['rackSpec']['overhangBack'];
  'rackingArea.rackSpec.topShelfHeight': RackingArea['rackSpec']['topShelfHeight'];
  'rackingArea.rackSpec.numColumnsInBay': RackingArea['rackSpec']['numColumnsInBay'];
  'rackingArea.rackSpec.numStationsInDepth': RackingArea['rackSpec']['numStationsInDepth'];

  // obstacle
  'obstacle.appearance': Obstacle['appearance'];
  'obstacle.wallMaterial': Obstacle['wallMaterial'];
  'box.height': number;
  'pillar.height': number;
  'wall.height': number;
  'wall.thickness': number;

  // flow - transport
  'flow.hourlyOrderCounts/transport': Flow['hourlyOrderCounts'][number];
  'flow.vehicleLimit/transport': Flow['vehicleLimit'];
  'flow.transportExtension.preAnnounceDuration': NonNullable<
    Flow['transportExtension']
  >['preAnnounceDuration'];
  'flow.transportExtension.maxActivePreAnnouncedAvOrders': NonNullable<
    Flow['transportExtension']
  >['maxActivePreAnnouncedAvOrders'];

  // flow - consolidation storage
  'flow.hourlyOrderCounts/consolidationStorage': Flow['hourlyOrderCounts'][number];
  'flow.vehicleLimit/consolidationStorage': Flow['vehicleLimit'];
  'flow.consolidationStorageExtension.batchSize': NonNullable<
    Flow['consolidationStorageExtension']
  >['batchSize'];
  'flow.consolidationStorageExtension.numConsolidations': NonNullable<
    Flow['consolidationStorageExtension']
  >['numConsolidations'];
  'flow.consolidationStorageExtension.preAnnounceDuration': NonNullable<
    Flow['consolidationStorageExtension']
  >['preAnnounceDuration'];
  'flow.consolidationStorageExtension.maxActivePreAnnouncedAvOrders': NonNullable<
    Flow['consolidationStorageExtension']
  >['maxActivePreAnnouncedAvOrders'];

  // flow - consolidation retrieval
  'flow.hourlyOrderCounts/consolidationRetrieval': Flow['hourlyOrderCounts'][number];
  'flow.vehicleLimit/consolidationRetrieval': Flow['vehicleLimit'];
  'flow.consolidationRetrievalExtension.shipmentSize': NonNullable<
    Flow['consolidationRetrievalExtension']
  >['shipmentSize'];
  'flow.consolidationRetrievalExtension.numConsolidations': NonNullable<
    Flow['consolidationRetrievalExtension']
  >['numConsolidations'];
  'flow.consolidationRetrievalExtension.retrievalStartDelayInHours': NonNullable<
    Flow['consolidationRetrievalExtension']
  >['retrievalStartDelayInHours'];

  // flow - goods to persons
  'flow.hourlyOrderCounts/goodsToPersons': number;
  'flow.vehicleLimit/goodsToPersons': Flow['vehicleLimit'];
  'flow.goodsToPersonsExtension.sequencingHorizonWindow': NonNullable<
    Flow['goodsToPersonsExtension']
  >['sequencingHorizonWindow'];
  'flow.goodsToPersonsExtension.handoverCooldown': NonNullable<
    Flow['goodsToPersonsExtension']
  >['handoverCooldown'];
  'flow.goodsToPersonsExtension.processDuration': NonNullable<
    Flow['goodsToPersonsExtension']
  >['processDuration'];

  // flow - goods to persons usage profiles
  'flow.goodsToPersonsExtension.processAreaUsage.type': UsageProfile['type'];
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.startWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['startWeight'];
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.endWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['endWeight'];
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileExponential.rate': NonNullable<
    UsageProfile['usageProfileExponential']
  >['rate'];

  'flow.goodsToPersonsExtension.aisleProfile.type': UsageProfile['type'];
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.startWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['startWeight'];
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.endWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['endWeight'];
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileExponential.rate': NonNullable<
    UsageProfile['usageProfileExponential']
  >['rate'];

  'flow.goodsToPersonsExtension.shelfProfile.type': UsageProfile['type'];
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.startWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['startWeight'];
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.endWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['endWeight'];
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileExponential.rate': NonNullable<
    UsageProfile['usageProfileExponential']
  >['rate'];

  'flow.goodsToPersonsExtension.depthProfile.type': UsageProfile['type'];
  'flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.startWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['startWeight'];
  'flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.endWeight': NonNullable<
    UsageProfile['usageProfileLinear']
  >['endWeight'];
  'flow.goodsToPersonsExtension.depthProfile.usageProfileExponential.rate': NonNullable<
    UsageProfile['usageProfileExponential']
  >['rate'];
  'flow.goodsToPersonsExtension.concurrentAisleCount': NonNullable<
    Flow['goodsToPersonsExtension']
  >['concurrentAisleCount'];

  // External flows
  'flow.hourlyOrderCounts/external': Flow['hourlyOrderCounts'][number];
  'flow.vehicleLimit/external': Flow['vehicleLimit'];
  'flow.externalFlowsExtension.processDuration': NonNullable<
    Flow['externalFlowExtension']
  >['processDuration'];

  // templates
  'template.lassie.numAisles': LassieParams['numAisles'];
  'template.lassie.numBays': LassieParams['numBays'];
  'template.lassie.numShelves': LassieParams['numShelves'];

  'template.lassieMini.numAisles': LassieMiniParams['numAisles'];
  'template.lassieMini.numBays': LassieMiniParams['numBays'];
  'template.lassieMini.numShelves': LassieMiniParams['numShelves'];

  'template.shuttle.numColumns': ShuttleRackParams['numColumns'];
  'template.shuttle.numLevels': ShuttleRackParams['numLevels'];
  'template.shuttle.numDeep': ShuttleRackParams['numDeep'];
  'template.shuttle.entrySide': ShuttleRackParams['entrySide'];

  'template.hik.numAisles': HikStorageParams['numAisles'];
  'template.hik.numBays': HikStorageParams['numBays'];
  'template.hik.numShelves': HikStorageParams['numShelves'];

  // Simulations
  'simulationConfig.duration': SimulationConfig['duration'];
  [key: `simulationConfig.vehicleAmount.${string}`]: number;

  // fmsConfig
  'simulationConfig.fmsConfig.obstructionTimeoutInSeconds': SimulationConfig['fmsConfig']['obstructionTimeOutInSeconds'];
  'simulationConfig.fmsConfig.failOnNoRouteFound': SimulationConfig['fmsConfig']['failOnNoRouteFound'];
  'simulationConfig.fmsConfig.manualIntervention': SimulationConfig['fmsConfig']['manualIntervention'];
  'simulationConfig.fmsConfig.distributionStrategy': SimulationConfig['fmsConfig']['distributionStrategy'];
  'simulationConfig.fmsConfig.hopsToBlockBeforeClaimedEndpoint': SimulationConfig['fmsConfig']['hopsToBlockBeforeClaimedEndpoint'];

  // uvtOverrides
  'simulationConfig.uvtOverrides.maxForwardSpeedPerc': SimulationConfig['uvtOverrides']['maxForwardSpeedPerc'];
  'simulationConfig.uvtOverrides.maxReverseSpeedPerc': SimulationConfig['uvtOverrides']['maxReverseSpeedPerc'];
  'simulationConfig.uvtOverrides.loadHandlingDurationPerc': SimulationConfig['uvtOverrides']['loadHandlingDurationPerc'];

  // export settings
  'exportSettings.generateTracks': boolean;
  'exportSettings.generateProximityMatrix': boolean;
  'exportSettings.generateTravelDurationCostMatrix': boolean;

  // cross-area
  'area.vehicleTypeId': string;
  'connection.smoothHeading': boolean;
};

export type PreferencesState = Partial<FddmPreferenceValues>;
export type StoredFddmPreferenceValues = Required<FddmPreferenceValues>;

/**
  Because of backend reasons, Preferences are not stored in Preferences, but instead in Preferences.additionalProperties. 
 */
export type FddmPreferences = {
  additionalProperties: Partial<FddmPreferenceValues>;
};

export const DEFAULT_FDDM_PREFERENCES: StoredFddmPreferenceValues = {
  'highwayArea.vehicleLimit': -1,
  'highwayArea.highwayPoiSpecs.crabSetting': 0,
  'highwayArea.highwayPoiSpecs.curvature': 0,
  'highwayArea.segment.width': 1,
  'highwayArea.highwayPoiSpecs.horizontalSpacing.minGap': 1.0,
  'highwayArea.highwayPoiSpecs.horizontalSpacing.minMargin': 0.5,
  'highwayArea.highwayPoiSpecs.verticalSpacing.minGap': 0.5,
  'highwayArea.highwayPoiSpecs.verticalSpacing.minMargin': 0.5,
  'highwayArea.highwayPoiSpecs.roadType': 'TWO_WAY_INDIA_STYLE',
  'highwayArea.connectNeighbors': 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY',
  'highwayArea.connectUturns': 'NO_UTURN_MAPPING',
  'highwayArea.highwayPoiSpecs.horizontalDistribution': 'GAP',
  'highwayArea.highwayPoiSpecs.verticalDistribution': 'MARGIN',

  'manualArea.loadHandlingType': 'DELIVERY',
  'manualArea.stationSpec.z': 0,
  'manualArea.stationSpec.loadHandlingDurationStrategyInSimulation': 'BY_HEIGHT',
  'manualArea.stationSpec.loadDurationOverrideInSimulation': 0,
  'manualArea.stationSpec.unloadDurationOverrideInSimulation': 0,

  'singularStationArea.constellationSpec.stacking': 'HORIZONTAL_MIDDLE',
  'singularStationArea.constellationSpec.distribution': 'GAP',
  'singularStationArea.constellationSpec.angle': 4.7123889804,
  'singularStationArea.constellationSpec.spacing.minGap': 0.75,
  'singularStationArea.constellationSpec.spacing.minMargin': 0.5,
  'singularStationArea.loadHandlingPoiSpecs.angle': 0,
  'singularStationArea.loadHandlingPoiSpecs.deltaX': 0,
  'singularStationArea.loadHandlingPoiSpecs.deltaY': 0,
  'singularStationArea.loadHandlingPoiSpecs.alwaysFitStation': false,
  'singularStationArea.loadHandlingPoiSpecs.crabSetting': 0,
  'singularStationArea.loadHandlingPoiSpecs.curvature': 0,
  'singularStationArea.loadHandlingPoiSpecs.canBeUsedForParking': false,
  'singularStationArea.loadHandlingPoiSpecs.parkingExtension.priority': 99,
  'singularStationArea.stationSpec.z': 0,
  'singularStationArea.stationSpec.loadHandlingDurationStrategyInSimulation': 'BY_HEIGHT',
  'singularStationArea.stationSpec.loadDurationOverrideInSimulation': 0,
  'singularStationArea.stationSpec.unloadDurationOverrideInSimulation': 0,
  'singularStationArea.stationSpec.processingExtension.processingDurationInSimulation': 15,
  'singularStationArea.connectNeighbors': 'NO_NEIGHBOR_MAPPING',

  'stationaryArea.constellationSpec.stacking': 'HORIZONTAL_MIDDLE',
  'stationaryArea.constellationSpec.distribution': 'GAP',
  'stationaryArea.constellationSpec.angle': 4.7123889804,
  'stationaryArea.constellationSpec.spacing.minGap': 1.25,
  'stationaryArea.constellationSpec.spacing.minMargin': 0.5,
  'stationaryArea.parkingExtension.priority': 1,
  'stationaryArea.chargingExtension.priority': 1,
  'stationaryArea.insertionExtension.waitDurationInSimulation': 0,
  'stationaryArea.extractionExtension.waitDurationInSimulation': 0,
  'stationaryArea.stationaryPoiSpecs.crabSetting': 0,
  'stationaryArea.stationaryPoiSpecs.curvature': 0,
  'stationaryArea.connectNeighbors': 'NO_NEIGHBOR_MAPPING',

  'rackingArea.rackType': 'ONE_SIDED_INSIDE',
  'rackingArea.rackingLoadHandlingType': 'STORAGE',
  'rackingArea.stacking': 'HORIZONTAL_MIDDLE',
  'rackingArea.margin': 0.5,
  'rackingArea.offsetDriving': 0,
  'rackingArea.alongsideExtension.aisleWidth': 2,
  'rackingArea.alongsideExtension.poiDirection': 'BOTH_DIRECTIONS',
  'rackingArea.insideExtension.forwardParking': false,
  'rackingArea.crabSetting': 0,
  'rackingArea.curvature': 0,
  'rackingArea.connectNeighbors': 'NO_NEIGHBOR_MAPPING',
  'rackingArea.canBeUsedForParking': false,
  'rackingArea.parkingExtension.priority': 99,

  'rackingArea.rackSpec.bayLength': 1.2,
  'rackingArea.rackSpec.bayDepth': 1.2,
  'rackingArea.rackSpec.bayHeight': 0.6,
  'rackingArea.rackSpec.beamLength': 1.0,
  'rackingArea.rackSpec.beamHeight': 0.1,
  'rackingArea.rackSpec.clearanceSide': 0.075,
  'rackingArea.rackSpec.clearanceInBetween': 0.075,
  'rackingArea.rackSpec.clearanceTop': 0.075,
  'rackingArea.rackSpec.clearanceDepth': 0,
  'rackingArea.rackSpec.overhangFront': 0,
  'rackingArea.rackSpec.overhangBack': 0,
  'rackingArea.rackSpec.topShelfHeight': 0,
  'rackingArea.rackSpec.numColumnsInBay': 1,
  'rackingArea.rackSpec.numStationsInDepth': 1,

  'obstacle.appearance': 'PILLAR',
  'obstacle.wallMaterial': 'CONCRETE',
  'box.height': 1,
  'pillar.height': 1,
  'wall.height': 1,
  'wall.thickness': 0.3,

  'flow.hourlyOrderCounts/transport': 30,
  'flow.vehicleLimit/transport': -1,
  'flow.transportExtension.preAnnounceDuration': 0,
  'flow.transportExtension.maxActivePreAnnouncedAvOrders': 1,

  'flow.hourlyOrderCounts/consolidationStorage': 30,
  'flow.vehicleLimit/consolidationStorage': -1,
  'flow.consolidationStorageExtension.batchSize': 30,
  'flow.consolidationStorageExtension.numConsolidations': 1,
  'flow.consolidationStorageExtension.preAnnounceDuration': 0,
  'flow.consolidationStorageExtension.maxActivePreAnnouncedAvOrders': 1,

  'flow.hourlyOrderCounts/consolidationRetrieval': 50,
  'flow.vehicleLimit/consolidationRetrieval': -1,
  'flow.consolidationRetrievalExtension.shipmentSize': 25,
  'flow.consolidationRetrievalExtension.numConsolidations': 1,
  'flow.consolidationRetrievalExtension.retrievalStartDelayInHours': 2,

  'flow.hourlyOrderCounts/goodsToPersons': 30,
  'flow.vehicleLimit/goodsToPersons': -1,
  'flow.goodsToPersonsExtension.sequencingHorizonWindow': 1200,
  'flow.goodsToPersonsExtension.handoverCooldown': 500,
  'flow.goodsToPersonsExtension.processDuration': 10,
  'flow.goodsToPersonsExtension.concurrentAisleCount': 1,

  'flow.goodsToPersonsExtension.processAreaUsage.type': 'LINEAR',
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.startWeight': 0.8,
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileLinear.endWeight': 0.2,
  'flow.goodsToPersonsExtension.processAreaUsage.usageProfileExponential.rate': 0.8,

  'flow.goodsToPersonsExtension.aisleProfile.type': 'LINEAR',
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.startWeight': 0.8,
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileLinear.endWeight': 0.2,
  'flow.goodsToPersonsExtension.aisleProfile.usageProfileExponential.rate': 0.8,

  'flow.goodsToPersonsExtension.shelfProfile.type': 'LINEAR',
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.startWeight': 1,
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileLinear.endWeight': 0.8,
  'flow.goodsToPersonsExtension.shelfProfile.usageProfileExponential.rate': 0.8,

  'flow.goodsToPersonsExtension.depthProfile.type': 'EXPONENTIAL',
  'flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.startWeight': 1,
  'flow.goodsToPersonsExtension.depthProfile.usageProfileLinear.endWeight': 0.8,
  'flow.goodsToPersonsExtension.depthProfile.usageProfileExponential.rate': 0.2,

  'flow.hourlyOrderCounts/external': 30,
  'flow.vehicleLimit/external': -1,
  'flow.externalFlowsExtension.processDuration': 10,

  'template.lassie.numAisles': 2,
  'template.lassie.numBays': 3,
  'template.lassie.numShelves': 12,

  'template.lassieMini.numAisles': 1,
  'template.lassieMini.numBays': 2,
  'template.lassieMini.numShelves': 4,

  'template.shuttle.numColumns': 12,
  'template.shuttle.numLevels': 8,
  'template.shuttle.numDeep': 10,
  'template.shuttle.entrySide': 'FRONT_AND_BACK',

  'template.hik.numAisles': 2,
  'template.hik.numBays': 3,
  'template.hik.numShelves': 12,

  // simulation
  'simulationConfig.duration': 1,

  'simulationConfig.fmsConfig.distributionStrategy': 0.7,
  'simulationConfig.fmsConfig.failOnNoRouteFound': false,
  'simulationConfig.fmsConfig.manualIntervention': true,
  'simulationConfig.fmsConfig.obstructionTimeoutInSeconds': 300,
  'simulationConfig.fmsConfig.hopsToBlockBeforeClaimedEndpoint': 3,

  'simulationConfig.uvtOverrides.loadHandlingDurationPerc': 100,
  'simulationConfig.uvtOverrides.maxForwardSpeedPerc': 100,
  'simulationConfig.uvtOverrides.maxReverseSpeedPerc': 100,

  'exportSettings.generateTracks': true,
  'exportSettings.generateProximityMatrix': false,
  'exportSettings.generateTravelDurationCostMatrix': false,

  'area.vehicleTypeId': '',
  'connection.smoothHeading': false,
} satisfies StoredFddmPreferenceValues;

export const getVehicleAmountPreferenceKey = (vehicleTypeId: string) =>
  `simulationConfig.vehicleAmount.${vehicleTypeId}` as const;

export const getDefaultFddmPreference = <TKey extends keyof StoredFddmPreferenceValues>(
  key: TKey,
): StoredFddmPreferenceValues[TKey] => {
  if (!Object.prototype.hasOwnProperty.call(DEFAULT_FDDM_PREFERENCES, key)) {
    // Dynamic template-literal keys (e.g. simulationConfig.vehicleAmount.${vehicleId})
    // have no static entry in DEFAULT_FDDM_PREFERENCES — return 0 so type validation passes.
    return 0 as unknown as StoredFddmPreferenceValues[TKey];
  }
  return DEFAULT_FDDM_PREFERENCES[
    key as keyof typeof DEFAULT_FDDM_PREFERENCES
  ] as StoredFddmPreferenceValues[TKey];
};
